import type {
  ApprovalDisplayStatus,
  ApprovalListTab,
  ApprovalPreviewKind,
  ApprovalPriority,
} from '../contracts/approvals';
import type { StatusTone } from '../contracts/common';
import type { ApprovalInitiatorType } from '../enums';
import { APPROVAL_STATUS_LABELS, APPROVAL_STATUS_TONES } from './events';

/** Status do wyświetlenia: statusy z bazy + „Wycofana” (odrzucona przez moduł, `withdrawn_at`). */
export const APPROVAL_DISPLAY_STATUS_LABELS: Record<ApprovalDisplayStatus, string> = {
  ...APPROVAL_STATUS_LABELS,
  withdrawn: 'Wycofana',
};

export const APPROVAL_DISPLAY_STATUS_TONES: Record<ApprovalDisplayStatus, StatusTone> = {
  ...APPROVAL_STATUS_TONES,
  withdrawn: 'neutral',
};

export const APPROVAL_PRIORITY_LABELS: Record<ApprovalPriority, string> = {
  normal: 'Zwykły',
  high: 'Wysoki',
};

export const APPROVAL_LIST_TAB_LABELS: Record<ApprovalListTab, string> = {
  pending: 'Czeka',
  history: 'Historia',
};

/** Kto przygotował akcję (meta karty: „przygotował Copilot”, „Worker”). */
export const APPROVAL_INITIATOR_LABELS: Record<ApprovalInitiatorType, string> = {
  user: 'Osoba',
  ai: 'Copilot',
  mcp: 'Agent (MCP)',
  worker: 'Worker',
};

/**
 * Etykiety typów akcji (meta karty Figmy 03: „Email · REA”, „Deploy · REA”, „Zadania · HelpMyCar”). Moduły dopisują
 * swoje typy tutaj; nieznany typ = „Akcja”.
 */
export const APPROVAL_TYPE_LABELS: Record<string, string> = {
  'email.send': 'Email',
  'maintenance.renewal_email': 'Email',
  'offers.send': 'Oferta',
  'offers.terms_change': 'Warunki oferty',
  'billing.issue_invoice': 'Faktura',
  'billing.mark_paid': 'Opłacenie',
  'work.task_proposals': 'Zadania',
  'context.memory_update': 'Pamięć',
  'deploy.production': 'Deploy',
  'data.delete': 'Usunięcie danych',
  'access.change': 'Zmiana dostępów',
  'client.archive': 'Archiwizacja klienta',
};

export function approvalTypeLabel(type: string): string {
  return APPROVAL_TYPE_LABELS[type] ?? 'Akcja';
}

function pluralPl(n: number, forms: readonly [one: string, few: string, many: string]): string {
  if (n === 1) return forms[0];
  const last = n % 10;
  const lastTwo = n % 100;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? forms[1] : forms[2];
}

const ACTIONS_FORMS = ['działanie', 'działania', 'działań'] as const;

/** „1 działanie”, „3 działania”, „5 działań”. */
export function approvalActionsCount(n: number): string {
  return `${n} ${pluralPl(n, ACTIONS_FORMS)}`;
}

/**
 * Stopka listy (Figma 03): „Wczoraj zatwierdzono 4 działania, odrzucono 1.” Bez decyzji wczoraj = null (bez stopki).
 */
export function approvalYesterdayText(s: { approved: number; rejected: number }): string | null {
  if (s.approved === 0 && s.rejected === 0) return null;
  const parts: string[] = [];
  if (s.approved > 0) parts.push(`zatwierdzono ${approvalActionsCount(s.approved)}`);
  if (s.rejected > 0) parts.push(`odrzucono ${s.rejected}`);
  return `Wczoraj ${parts.join(', ')}.`;
}

/** Podtytuł na mobile (Figma 03 mobile): „3 działania AI czekają na Twoją zgodę”. */
export function approvalsWaitingText(n: number): string {
  if (n === 0) return 'Nic nie czeka na Twoją zgodę';
  const verb = pluralPl(n, ['czeka', 'czekają', 'czeka']);
  return `${approvalActionsCount(n)} AI ${verb} na Twoją zgodę`;
}

/** Główny przycisk karty wg rodzaju podglądu (Figma 03: „Zatwierdź i wyślij”, mobile wdrożenie: „Wdróż”). */
export const APPROVAL_PRIMARY_ACTION_LABELS: Record<ApprovalPreviewKind, string> = {
  email: 'Zatwierdź i wyślij',
  invoice: 'Zatwierdź i wystaw',
  task_proposals: 'Zatwierdź i dodaj',
  memory: 'Zatwierdź i zapisz',
  offer_terms: 'Zatwierdź i zmień',
  deploy: 'Zatwierdź i wdróż',
  generic: 'Zatwierdź i wykonaj',
};

/** Etykieta rodzaju akcji na mobile („Akceptacja · wdrożenie”). */
export const APPROVAL_PREVIEW_KIND_LABELS: Record<ApprovalPreviewKind, string> = {
  email: 'mail',
  invoice: 'faktura',
  task_proposals: 'zadania',
  memory: 'pamięć',
  offer_terms: 'warunki oferty',
  deploy: 'wdrożenie',
  generic: 'działanie',
};
